import * as React from 'react'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { isRichTextBlock, type Block } from '@basehub/data/replicache/index'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
  ContextMenuPortal,
} from '~/sotano-kit/primitives/context-menu'
import { getBlockMenuActions } from './actions'
import { normalizeStringPath } from '@basehub/data/replicache/index'
import Link from 'next/link'
import { useTabsStore, useTabsStoreRoot } from '../../tabs/store'
import { useMutateWithErrorHandling } from '~/replicache/frontend/use-mutate-with-error-handling'
import { getId } from '@basehub/utils/id/index'
import { useEditorMode } from '../../editor-mode-provider'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { isPublicRepo } from '@basehub/data/replicache/mutators/_public-types'
import { useIsGuestInHisBranch } from '~/hooks-data/use-user-role'
import { useUserPreferences } from '~/hooks-data/use-user-preferences'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { clsx } from 'clsx'
import { ChevronRightIcon } from '@radix-ui/react-icons'
import { useEditorStore } from '../../editor-store'
import { useRepoStore } from '../../repo-store'
import { useToggleState } from '~/hooks/use-toggle-state'
import { ConvertToConfirmationDialog } from '../rich-text/confirmation-dialog'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import {
  RepoUndoOps,
  useRepoUndoManager,
} from '~/replicache/frontend/undo-redo/manager'
import { DeleteBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import {
  useBranchOrCommitSegment,
  useSelectedBranch,
} from '~/lib/commits/use-selected-branch-or-commit'
import { ReferenceInputStoreContext } from '../reference/input'
import { usePlaygroundCookie } from '~/lib/playground/use-playground-id'
import { useSelectedVariants } from '~/hooks/use-selected-variants'

export const BlockWrapperContextMenu = ({
  children,
  block,
  path,
  parentBlock,
  canOpenPropertiesPanel,
}: {
  children: React.ReactNode
  block: Block
  path: string
  parentBlock: Block
  canOpenPropertiesPanel: boolean
}) => {
  const {
    data,
    repoReplicache: replicache,
    isLoading: isLoadingSelectedRepo,
  } = useSelectedTeamAndRepo()
  const repo = data?.repo
  const collapsePropertiesPanel = useUserPreferences((s) =>
    canOpenPropertiesPanel ? (s.collapsePropertiesPanel ?? false) : false
  )
  const userId = useUserPreferences((s) => s.id)
  const undoManager = useRepoUndoManager()
  const { teamSlug, repoSlug, isLoading } = useDashboardSlugs()
  const { encodedSegment } = useBranchOrCommitSegment()
  const setSelectedBlockId = useEditorStore((s) => s.setSelectedBlockId)
  const setIsRenamingBlockId = useEditorStore((s) => s.setIsRenamingBlockId)
  const setFocus = useRepoStore((s) => s.setFocus)
  const editorMode = useEditorMode()
  const triggerRef = React.useRef<HTMLDivElement>(null)
  const tabsStore = useTabsStoreRoot()
  const activeTabKey = useTabsStore((s) => s.activeTab?.key)
  const sourceBlockId = idToSourceBlockId(block)
  const referenceStore = React.useContext(ReferenceInputStoreContext)
  const handleRemoveReferenceTarget =
    referenceStore?.getState().onRemoveTarget ?? null
  const playgroundId = usePlaygroundCookie()
  const branch = useSelectedBranch().branch
  const isPlayground = Boolean(
    !!playgroundId && (!!branch?.playgroundId || branch?.isDefault)
  )

  const focusOp = React.useMemo(() => {
    return {
      op: 'focus',
      args: {
        focus: {
          blockId: sourceBlockId,
          type: 'block:wrapper',
        },
        preferredTabKey: activeTabKey,
      },
    } as RepoUndoOps[number]
  }, [activeTabKey, sourceBlockId])

  const { mutateAsync: handleDelete } = useMutateWithErrorHandling(
    async (mutate, id: string) => {
      const params: DeleteBlockParams = {
        id,
        path,
        idSeed: getId(),
        isoNow: new Date().toISOString(),
      }
      const result = await mutate.deleteBlock(params)
      if (result?.undo) {
        undoManager.current?.push({
          redo: [focusOp, { op: 'deleteBlock', args: params }],
          undo: [
            { op: 'blockBatchMutations', args: { mutations: result.undo } },
            focusOp,
          ],
        })
      }
      return result
    }
  )
  const treeManagerRef = useWorkingTreeStore((s) => s.treeRef)
  const toggleState = useToggleState()
  const isGuestInHisBranch = useIsGuestInHisBranch()

  const selectedVariants = useSelectedVariants({ parentBlock })

  const actions = React.useMemo(() => {
    if (!repoSlug || !teamSlug || isLoading || isLoadingSelectedRepo) {
      return []
    }

    const normalizedPath = normalizeStringPath({
      path,
      edgeId: block.id,
      includeEdge: false,
    })
    const parentBlockId = normalizedPath[normalizedPath.length - 1]
    if (!parentBlockId) return []

    if (isPublicRepo(repo) && !isGuestInHisBranch && !isPlayground) {
      return getBlockMenuActions({
        treeManagerRef,
        configureAccess: false,
        blocks: [block],
        repoSlug,
        teamSlug,
        branchOrCommitSegment: encodedSegment,
        parentBlockId,
        repoStore: { setFocus },
        parentBlock,
        tabsStore,
        undoManager: undoManager.current,
        selectedVariants,
      })
    }

    if (
      !replicache ||
      collapsePropertiesPanel === undefined ||
      userId === undefined
    ) {
      return []
    }
    return getBlockMenuActions({
      treeManagerRef,
      blocks: [block],
      path,
      replicache,
      collapsePropertiesPanel,
      selectedVariants,
      userId,
      repoSlug,
      teamSlug,
      branchOrCommitSegment: encodedSegment,
      undoManager: undoManager.current,
      parentBlockId,
      editorStore: {
        setSelectedBlockId,
        setIsRenamingBlockId,
      },
      editorMode,
      configureAccess: true,
      repoStore: { setFocus },
      urFocusOpStart: focusOp,
      parentBlock,
      tabsStore,
      handleDelete: async () => {
        await handleDelete(block.id)
      },
      handleRemoveReferenceTarget: async () => {
        await handleRemoveReferenceTarget?.(idToSourceBlockId(block))
      },
      handleRichTextConvertToPlainText: toggleState.handleOn,
    })
  }, [
    selectedVariants,
    repoSlug,
    teamSlug,
    isLoading,
    isLoadingSelectedRepo,
    path,
    block,
    repo,
    isGuestInHisBranch,
    isPlayground,
    replicache,
    collapsePropertiesPanel,
    userId,
    treeManagerRef,
    encodedSegment,
    undoManager,
    setSelectedBlockId,
    setIsRenamingBlockId,
    editorMode,
    setFocus,
    focusOp,
    parentBlock,
    tabsStore,
    toggleState.handleOn,
    handleDelete,
    handleRemoveReferenceTarget,
  ])

  React.useEffect(() => {
    // stop propagation of contextmenu on some specific elements
    if (!triggerRef.current || block.type === 'og-image') return

    const allInputs = triggerRef.current.querySelectorAll<HTMLElement>(
      'input, textarea, [contenteditable]'
    )
    const propertiesPanel = triggerRef.current.querySelector<HTMLElement>(
      '#repo-content-sidebar'
    )

    const stopPropagation = (e: MouseEvent) => {
      e.stopPropagation()
    }

    allInputs.forEach((input) => {
      input.addEventListener('contextmenu', stopPropagation)
    })

    propertiesPanel?.addEventListener('contextmenu', stopPropagation)

    return () => {
      allInputs.forEach((input) => {
        input.removeEventListener('contextmenu', stopPropagation)
      })

      propertiesPanel?.removeEventListener('contextmenu', stopPropagation)
    }
  }, [block.type])

  return (
    <>
      <ContextMenu>
        <ContextMenuTrigger ref={triggerRef} asChild>
          {children}
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ContextMenuLabel className="text-ellipsis whitespace-nowrap overflow-hidden">
            <span className="text-dark-muted">
              {editorMode === 'write:all' && 'Editing '}
            </span>{' '}
            {block.type === 'comment' ? 'comment' : block.title || 'Untitled'}
          </ContextMenuLabel>

          {actions.map((action) => {
            switch (action.type) {
              case 'action':
                return (
                  <ContextMenuItem
                    className="space-x-2 text-xs leading-4"
                    key={action.key}
                    onSelect={() => {
                      setSelectedBlockId(idToSourceBlockId(block))
                      setTimeout(() => action.onSelect())
                    }}
                  >
                    {action.icon}
                    <span>{action.label}</span>
                  </ContextMenuItem>
                )
              case 'action-link':
                return (
                  <Link
                    key={action.key}
                    onClick={action.onSelect}
                    href={action.href}
                  >
                    <ContextMenuItem className="space-x-2 text-xs leading-4">
                      {action.icon}
                      <span>{action.label}</span>
                    </ContextMenuItem>
                  </Link>
                )

              case 'separator':
                return <ContextMenuSeparator key={action.key} />

              case 'submenu':
                return (
                  <ContextMenuSub key={action.key}>
                    <ContextMenuSubTrigger asChild>
                      <span className="space-x-2 text-xs leading-4">
                        {action.icon}
                        <span>{action.label}</span>
                        <ChevronRightIcon
                          width={12}
                          height={12}
                          className="text-dark-muted !ml-auto"
                        />
                      </span>
                    </ContextMenuSubTrigger>

                    <ContextMenuPortal>
                      <ContextMenuSubContent>
                        {action.items.map((sub) => {
                          if (sub.type === 'separator') {
                            return <ContextMenuSeparator key={sub.key} />
                          }

                          if (sub.type !== 'action') {
                            throw new Error('Invalid action type')
                          }

                          return (
                            <ContextMenuItem
                              key={sub.key}
                              className={clsx(
                                'space-x-2 text-xs leading-4',
                                sub.intent === 'danger' && 'text-dark-error'
                              )}
                              onSelect={sub.onSelect}
                              onClick={sub.onClick}
                            >
                              {sub.icon}
                              <span>{sub.label}</span>
                            </ContextMenuItem>
                          )
                        })}
                      </ContextMenuSubContent>
                    </ContextMenuPortal>
                  </ContextMenuSub>
                )

              default:
                console.error({ action })
                throw new Error('Unknown action type')
            }
          })}
        </ContextMenuContent>
      </ContextMenu>
      {isRichTextBlock(block) && (
        <ConvertToConfirmationDialog
          block={block}
          onConfirm={() => {
            const action = actions.find(
              (a) => a.key === 'convert-to-plain-text'
            )
            if (action && 'onSelect' in action) {
              action.onSelect({ confirmed: true })
            }
          }}
          toggleState={toggleState}
        />
      )}
    </>
  )
}

export default BlockWrapperContextMenu
